import { useEffect, useMemo, useState } from "react";
import type { LinkableEmployee, LinkableEmployeesResponse } from "../lib/types";
import { fmtSgtDateTime } from "../lib/datetime";

/**
 * Chooser for the employees an allowlist email may be linked to: staff HR has
 * no email for who are still employed. The pool is fetched on open, not on
 * mount — the HR source takes several seconds per call.
 */
export function EmployeePicker({
  value,
  onChange,
  load,
  disabled,
}: {
  value: string | null;
  onChange: (employeeNo: string | null) => void;
  load: () => Promise<LinkableEmployeesResponse>;
  disabled?: boolean;
}) {
  const [data, setData] = useState<LinkableEmployeesResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let live = true;
    setLoading(true);
    setErr(null);
    load()
      .then((d) => live && setData(d))
      .catch((e) => live && setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [load]);

  const employees = useMemo(() => {
    const all = data?.employees ?? [];
    const q = filter.trim().toLowerCase();
    return q ? all.filter((e) => e.employeeNo.toLowerCase().includes(q)) : all;
  }, [data, filter]);

  if (loading) return <span className="muted">Loading employees…</span>;
  if (err) {
    return (
      <span className="error">
        Employee list unavailable: {err}
      </span>
    );
  }
  if ((data?.employees.length ?? 0) === 0) {
    return (
      <span className="muted">
        No linkable employees — every employee HR knows about either has an
        email of their own or has left.
      </span>
    );
  }

  return (
    <span className="picker">
      {(data?.employees.length ?? 0) > 12 && (
        <input
          type="search"
          placeholder="filter no."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          disabled={disabled}
          size={10}
        />
      )}
      <select
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">— not linked —</option>
        {employees.map((e) => (
          <option key={e.employeeNo} value={e.employeeNo}>
            {describe(e)}
          </option>
        ))}
      </select>
      {data?.syncedAt && (
        <span className="muted">HR data: {fmtSgtDateTime(data.syncedAt)}</span>
      )}
    </span>
  );
}

function describe(e: LinkableEmployee): string {
  const status = e.employmentStatus.replace(/^\d+\.\s*/, "");
  const until = e.lastWorkingDay ? ` — until ${e.lastWorkingDay}` : "";
  return `#${e.employeeNo} · ${status}${until}`;
}
